import React, { useCallback } from 'react';

// Status color mapping
const getStatusColor = (status) => {
  switch (status) {
    case 'available':
      return '#4CAF50';
    case 'reserved':
      return '#FF9800';
    case 'sold':
      return '#F44336';
    default:
      return '#9E9E9E';
  }
};

// Individual property region component
function PropertyRegion({ 
  property, 
  isSelected, 
  isHovered,
  onClick, 
  onHover 
}) {
  const handleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick(property.id, event);
  }, [property.id, onClick]);

  const handleMouseEnter = useCallback((event) => {
    event.preventDefault();
    onHover(property.id);
  }, [property.id, onHover]);

  const handleMouseLeave = useCallback((event) => {
    event.preventDefault();
    onHover(null);
  }, [onHover]);

  const handleTouchStart = useCallback((event) => {
    event.preventDefault();
    onHover(property.id);
  }, [property.id, onHover]);

  const handleTouchEnd = useCallback((event) => {
    event.preventDefault();
    onClick(property.id, event);
    // Clear hover state after touch interaction
    setTimeout(() => onHover(null), 200);
  }, [property.id, onClick, onHover]);

  if (!property.mapCoordinates?.svgPath) {
    return null;
  }

  const statusColor = getStatusColor(property.status?.code);
  const isAvailable = property.status?.code === 'available';
  
  // Use status color from config
  const finalColor = statusColor;
  
  return (
    <g className="property-region-group">
      <path
        d={property.mapCoordinates.svgPath}
        className={`property-region ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${!isAvailable ? 'unavailable' : ''}`}
        fill={finalColor}
        style={{ 
          cursor: isAvailable ? 'pointer' : 'not-allowed'
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
      
    </g>
  );
}

// Main SVG overlay component
function PropertySVGOverlay({
  properties = [],
  selectedProperty,
  hoveredProperty,
  onPropertyClick,
  onPropertyHover,
  viewBox = '0 0 100 56.5'
}) {
  // Validate SVG path
  const isValidSVGPath = useCallback((path) => {
    if (!path || typeof path !== 'string') return false;
    
    try {
      // Basic validation - check if it looks like an SVG path
      return path.trim().length > 0 && /^[MmLlHhVvCcSsQqTtAaZz0-9\s,.-]+$/.test(path);
    } catch (error) {
      console.warn('Invalid SVG path:', path, error);
      return false;
    }
  }, []);

  // Filter properties with valid coordinates
  const validProperties = properties.filter(property => 
    property.mapCoordinates && 
    isValidSVGPath(property.mapCoordinates.svgPath)
  );

  if (validProperties.length === 0) {
    return null;
  }

  return (
    <svg 
      className="property-overlay" 
      viewBox={viewBox}
      style={{ pointerEvents: 'none' }}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background for testing (remove in production) */}
      <defs>
        <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
          <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#f0f0f0" strokeWidth="1" opacity="0.1"/>
        </pattern>
      </defs>
      
      {validProperties.map(property => (
        <PropertyRegion
          key={property.id}
          property={property}
          isSelected={selectedProperty === property.id}
          isHovered={hoveredProperty === property.id}
          onClick={onPropertyClick}
          onHover={onPropertyHover}
        />
      ))}
    </svg>
  );
}

export default PropertySVGOverlay;